// Import class dependencies
///////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////
import gsap from 'gsap';

/**
 * Creates a new Drawer
 * @class
 */
class Drawer {

    /**
     * @constructor
     * @param {HTMLElement} el : the drawer HTML element
     * @param {Object} params - the object options configuration
     */
    constructor(el, params) {

        this.el = el ? el : this.handleErrors('no_element');
        this.params = this.getParams(params)
        this.animation = null
        this.isActive = false

        if (!window.allDrawer) {
            window.allDrawer = []
        }

        window.allDrawer.push(this)

        this.init()

    }

    /**
     * @function
     * @name init : main init function
     */
    init() {
        this.initAnimation()
        this.initEvents()
    }

    /**
     * @function
     * @name initEvents : init all events
     */
    initEvents() {
        this.handleToggle()
        this.handleClickOutside()

        if (this.params.closeBtnCSSSelector) {
            this.handleCloseBtn()
        }
    }

    /**
     * @function
     * @name handleToggle used to toggle (open/close) the drawer
     */
    handleToggle() {

        const btns = document.querySelectorAll(this.params.toggleBtnCSSSelector)

        if (!btns.length) {
            this.handleErrors('no_toggle_button')
        }

        btns.forEach(btn => {

            btn.addEventListener('click', (e) => {
                e.preventDefault()

                if (btn.classList.contains(this.params.activeBtnClass)) {
                    this.close()
                } else {
                    this.open(btn)
                }
            })

        })

    }

    handleClickOutside() {
        document.addEventListener('click', (e) => {
            const target = e.target
            if(target.closest('.drawer') || target.closest(this.params.toggleBtnCSSSelector) || target.closest('.mce-container') || target.closest('#wp-link-wrap')) {
                return
            }
            this.close()
        })
    }

    /**
     * @function
     * @name handleCloseBtn used to handle isolated close btn
     */
    handleCloseBtn() {
        const closeBtnSelectorFormated = this.params.closeBtnCSSSelector.substring(1)
        if(this.el) {
            this.el.addEventListener('click', (e) => {
                if (e.target.classList.contains(closeBtnSelectorFormated) || e.target.closest(this.params.closeBtnCSSSelector)) {
                    e.preventDefault()
                    this.close()
                    this.dispatchCustomEvent('closeclicked', this.el)
                }
            })
        }
    }

    /**
     * @function
     * @name initAnimation used to create the drawer animation
     */
    initAnimation() {

        const timeline = gsap.timeline({paused: true})

        timeline.set('body', {overflow: 'hidden'})
                .set(this.el, {autoAlpha: 1})
                .from(this.el, {x: '100%', duration: 0.4, ease: 'Power2.easeOut'})

        this.timeline = timeline

    }

    /**
     * @function
     * @name open used to open the drawer
     */
    open(btn = false) {

        this.closeAll()

        this.timeline.timeScale(1).play()

        const btns = document.querySelectorAll(this.params.toggleBtnCSSSelector)

        btns.forEach(btn => {
            btn.classList.add(this.params.activeBtnClass)
        })

        this.dispatchCustomEvent('opened', this.el, {
            clickedEl: btn
        })
        this.isActive = true

    }

    /**
     * @function
     * @name close used to close the drawer
     */
    close() {

        this.timeline.timeScale(2).reverse()

        const btns = document.querySelectorAll(this.params.toggleBtnCSSSelector)

        btns.forEach(btn => {
            btn.classList.remove(this.params.activeBtnClass)
        })

        this.dispatchCustomEvent('closed', this.el)
        this.isActive = false

    }

    /**
     * @function
     * @name closeAll used to close all drawer instance before open new one
     */
    closeAll() {

        const allDrawer = window.allDrawer

        for (let index = 0; index < allDrawer.length; index++) {
            const drawer = allDrawer[index];

            if (drawer.isActive) {
                drawer.close()
            }
        }

    }

    /**
     * @function
     * @name on used to listen custom event on el
     * @param {string} eventName : name of event
     * @param {function} callback : function executed when this event is triggered
     *
     * Available events : 'opened', 'closed', 'closeclicked'
     */
    on(eventName, callback) {
        this.el.addEventListener(eventName, callback)
    }

    /**
     * @function
     * @name getParams Get params to configure the class
     * @param {object} object passed in constructor
     * @return {object} mix between default paramas and params in constructor
     */
    getParams(params) {

        const defaultParams = {
            toggleBtnCSSSelector: '.js-drawer-btn',
            closeBtnCSSSelector: false,
            activeBtnClass: '--active'
        }

        return {...defaultParams, ...params}

    }

    /**
     * @function
     * @name dispatchCustomEvent used to dispatch custom event on element
     * @param {string} eventName : name of event dispatched
     * @param {HTMLDivElement} element : HTML element to dispatch event on
     * @param {Object} detailObject : custom detail object for event
     */
    dispatchCustomEvent(eventName, element, detailObject = null){
        const event = new CustomEvent(eventName, { detail: detailObject })
        element.dispatchEvent(event);
    }

    /**
     * @function
     * @name handleErrors console log message errors by keys
     * @param {string} key of errors
     */
    handleErrors(key) {

        const errors = {
            no_element: "[Drawer] - The container element doesn't exist in DOM. Please provide an existing HTML Element",
            no_toggle_button: "[Drawer - Can't find the toggle button in DOM. Please provide an existing HTML Element]"
        }

        console.info(errors[key]);

    }


}

export default Drawer;